import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, BedDouble, Bath, Layers, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const lb = (o, lang) => (o ? (lang === "es" ? o.es : o.en) : "");

function Card({ d, lang, t, onOpen, i }) {
  return (
    <button type="button" onClick={onOpen} style={{ animationDelay: `${i * 80}ms` }} data-testid={`gallery-item-${d.id}`}
      className="group text-start rounded-3xl overflow-hidden bg-white border border-slate-200 animate-in fade-in slide-in-from-bottom-4 duration-700 fill-mode-both">
      <div className="aspect-[4/3] overflow-hidden bg-slate-100">
        <img src={`${API}/api/home-designs/${d.id}/image`} alt={`AI design: ${lb(d.style, "en")} home, ${lb(d.view, "en")}`} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      </div>
      <div className="p-4">
        <p className="font-semibold text-slate-900">{lb(d.style, lang)}</p>
        <p className="text-sm text-slate-500">{lb(d.view, lang)}{d.pool && ` · ${lb(d.pool, lang)}`}</p>
        <div className="mt-2 flex gap-4 text-xs text-slate-600">
          <span className="inline-flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" />{d.bedrooms}</span>
          <span className="inline-flex items-center gap-1"><Bath className="h-3.5 w-3.5" />{d.bathrooms}</span>
          <span className="inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" />{d.floors} {t("dz.floors").toLowerCase()}</span>
        </div>
      </div>
    </button>
  );
}

function Viewer({ d, lang, t, onClose }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", esc); document.body.style.overflow = ""; };
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[80] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="gallery-viewer">
      <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute -top-11 end-0 text-white p-2" aria-label="Close" data-testid="gallery-viewer-close"><X className="h-6 w-6" /></button>
        <img src={`${API}/api/home-designs/${d.id}/image`} alt={`AI design: ${lb(d.style, "en")} home`} className="w-full max-h-[75vh] object-contain rounded-2xl" />
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-white">
          <p className="font-semibold">{lb(d.style, lang)} · <span className="font-normal text-slate-300">{lb(d.view, lang)}</span></p>
          <Link to="/build/design" className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold px-5 h-11 transition-colors" data-testid="gallery-viewer-cta"><Sparkles className="h-4 w-4" />{t("dz.g_like")}</Link>
        </div>
      </div>
    </div>
  );
}

export function DesignGallery() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/home-designs/gallery").then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="design-gallery">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-700">{t("dz.kicker")}</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("dz.g_title")}</h2>
          <p className="mt-1 text-slate-600 text-sm sm:text-base">{t("dz.g_sub")}</p>
        </div>
        <Link to="/build/design" className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 h-11 transition-colors" data-testid="gallery-cta-btn"><Sparkles className="h-4 w-4" />{t("dz.cta")}</Link>
      </div>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((d, i) => <Card key={d.id} d={d} i={i} lang={lang} t={t} onOpen={() => setOpen(d)} />)}
      </div>
      {open && <Viewer d={open} lang={lang} t={t} onClose={() => setOpen(null)} />}
    </section>
  );
}
